/**
 * "SwiftOps is also a mobile app" — shown ONCE, over the dashboard.
 *
 * Someone who joins from an invite email has no way of knowing the app exists
 * (the store listing is titled "Swiftlane Operations", so searching "SwiftOps"
 * does not reliably find it). Setting a password is the one moment they are
 * guaranteed to be looking, so that is when we say it.
 *
 * It used to say it as a full-page step BETWEEN the password form and the app,
 * with a "Continue to SwiftOps" button — which made a nudge into a gate. PO,
 * 2026-08-15: *"it is block to go to dashboard."* So the invite now lands
 * straight in the product and this rides on top of it: dismissible, closable on
 * Esc and on click-outside, and gone for good once seen.
 *
 * The one-shot flag is set by `AcceptInvitePage` immediately before its hard
 * reload, and read here after that reload — `sessionStorage`, so it is scoped to
 * the tab that just accepted, survives the reload, dies with the tab, and holds
 * no credential. Every access is guarded because Safari private mode can throw
 * on the whole API.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Space, Typography } from 'antd';
import { MobileOutlined } from '@ant-design/icons';
import { detectMobilePlatform } from '../lib/appStoreLinks';
import { useAuth } from '../context/AuthContext';
import StoreBadge from './StoreBadge';

const { Text } = Typography;

const PENDING_KEY = 'swiftops.showAppDownload';

/**
 * Ask for the prompt on the next authenticated paint. Safe to call twice.
 * Stamped with the user id so it can only ever open for the person who asked.
 */
export function requestAppDownloadPrompt(userId: string): void {
  try {
    sessionStorage.setItem(PENDING_KEY, userId);
  } catch {
    // Private mode — the person simply doesn't get the nudge. Never worth
    // failing an accepted invite over.
  }
}

function isPromptPending(userId: string | undefined): boolean {
  try {
    const stored = sessionStorage.getItem(PENDING_KEY);
    if (!stored) return false;
    // Scoped to the person who asked for it. If the post-accept load never
    // reached the app (a 500 on /auth/me bounces to /login), the flag survives
    // in the tab — and without this check it would open over the dashboard of
    // whoever signed in next.
    if (stored !== userId) {
      clearPrompt();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function clearPrompt(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* private mode — nothing to clear */
  }
}

export default function AppDownloadModal() {
  const { t } = useTranslation(['auth']);
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const userId = user?.id;
  useEffect(() => {
    if (isPromptPending(userId)) setOpen(true);
  }, [userId]);

  // Retired when they CLOSE it, not when it is read: consuming on read meant a
  // reload in the second before they finished reading lost the only notice the
  // web app gives that a mobile app exists. Closing is a decision; a reload is
  // not. Account Settings carries the badges permanently either way.
  const dismiss = () => {
    clearPrompt();
    setOpen(false);
  };

  if (!open) return null;

  // Emphasis only — BOTH badges always render, so a wrong or unknown guess
  // never hides a store from anyone (iPadOS lands on 'other' by design). The
  // detected platform decides the ORDER, nothing else.
  const stores: Array<'ios' | 'android'> =
    detectMobilePlatform() === 'android' ? ['android', 'ios'] : ['ios', 'android'];

  return (
    <Modal
      open={open}
      onCancel={dismiss}
      // Dismissal is the whole point of this change: Esc, the ✕, click-outside
      // and an explicit button all close it, and nothing is lost by closing.
      footer={null}
      title={
        <Space>
          <MobileOutlined style={{ color: 'var(--brand)' }} />
          {t('auth:invite.appModalTitle')}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" className="w-full">
        <Text type="secondary">{t('auth:invite.successSubtitle')}</Text>

        <Space size="small" wrap>
          {stores.map((store) => (
            <StoreBadge key={store} store={store} />
          ))}
        </Space>

        <Button type="link" onClick={dismiss} style={{ paddingInline: 0 }}>
          {t('auth:invite.appModalDismiss')}
        </Button>
      </Space>
    </Modal>
  );
}
